import { useEffect, useRef, useState } from 'react'
import type { CustomCellEditorProps } from 'ag-grid-react'
import type { ColDef, ValueSetterParams } from 'ag-grid-community'
import {
  normalizeTnvedCode,
  type ChestnyZnak,
  type CustomsAnswerPatchDto,
  type CustomsQueueRow,
} from '@crm/shared'
import { TextareaEditor } from '../table/TextareaEditor'
import { toast } from '../../shared/ui/toastStore'
import { dutyFractionPercent, parseDutyPercent } from './components/customsAnswerForm'
import { znakOf, type CustomsGridRow } from './customsGridRow'

/**
 * Правка ячеек ответа прямо в очереди «Нетарифка» (логист, 27.09.2026): двойной
 * клик по ТН ВЭД, пошлине, сбору, «Честному знаку», «Нетарифке», «Сертификату»,
 * «Особенностям» — как менеджер правит «Наименование» в карточке заявки.
 *
 * Ячейка сама значение не меняет (valueSetter → false): ввод разбирается здесь,
 * правка уходит на сервер (`PATCH /customs/items/:id/answer`), строка обновляется
 * из ответа сервера. Ошибка ввода — тост, в ячейке остаётся прежнее значение.
 */

export type CustomsEditHandler = (row: CustomsQueueRow, patch: CustomsAnswerPatchDto) => void

/** Разбор ввода: правка для сервера, «ничего не изменилось» или текст ошибки. */
export type InlineParse = { patch: CustomsAnswerPatchDto } | { skip: true } | { error: string }

const SKIP: InlineParse = { skip: true }

const text = (v: unknown): string => (v == null ? '' : String(v)).trim()
const isEmpty = (s: string) => s === '' || s === '—'

export function parseTnvedInput(value: unknown, row: CustomsQueueRow): InlineParse {
  const raw = text(value)
  const own = row.check?.tnvedCode ?? null
  if (isEmpty(raw)) return own ? { patch: { tnvedCode: null } } : SKIP
  const code = normalizeTnvedCode(raw)
  if (!code) return { error: 'Код ТН ВЭД — 10 цифр' }
  return code === own ? SKIP : { patch: { tnvedCode: code } }
}

/**
 * Пошлина: «8», «7,5», «8 %» — проценты; «0,08» без знака % — доля, как пишет
 * брокер в файле (тоже 8 %). Пусто — стереть.
 */
export function parseDutyInput(value: unknown, row: CustomsQueueRow): InlineParse {
  const raw = text(value)
  const own = row.check?.dutyRate ?? null
  if (isEmpty(raw)) return own != null ? { patch: { dutyRate: null } } : SKIP
  const fraction = raw.includes('%') ? null : dutyFractionPercent(raw)
  const rate = parseDutyPercent(fraction != null ? String(fraction).replace('.', ',') : raw)
  if (rate == null) return { error: 'Пошлина — число процентов от 0 до 100, например 8 или 7,5' }
  return rate === own ? SKIP : { patch: { dutyRate: rate } }
}

export type FeeChoice = 'UNKNOWN' | 'ZERO' | 'RAISED'

export const FEE_CHOICES: Array<{ value: FeeChoice; label: string }> = [
  { value: 'UNKNOWN', label: 'Не указан' },
  { value: 'ZERO', label: '0' },
  { value: 'RAISED', label: 'Повышенный (73 860 ₽)' },
]

export function feeChoiceOf(row: CustomsQueueRow): FeeChoice {
  const raised = row.check?.customsFeeRaised
  return raised == null ? 'UNKNOWN' : raised ? 'RAISED' : 'ZERO'
}

export function parseFeeInput(value: unknown, row: CustomsQueueRow): InlineParse {
  const choice = text(value) as FeeChoice
  if (!FEE_CHOICES.some((c) => c.value === choice)) return SKIP
  if (choice === feeChoiceOf(row)) return SKIP
  const raised = choice === 'UNKNOWN' ? null : choice === 'RAISED'
  return { patch: { customsFeeRaised: raised } }
}

export const ZNAK_CHOICES: Array<{ value: ChestnyZnak; label: string }> = [
  { value: 'YES', label: 'Нужен' },
  { value: 'NO', label: 'Не нужен' },
  { value: 'UNKNOWN', label: 'Неизвестно' },
]

export function parseZnakInput(value: unknown, row: CustomsQueueRow): InlineParse {
  const choice = text(value) as ChestnyZnak
  if (!ZNAK_CHOICES.some((c) => c.value === choice)) return SKIP
  // Своё значение в записанном ответе то же — писать нечего. Без своего ответа
  // выбор = ответ (даже если совпал с памятью: логист его подтвердил).
  const own = row.check?.checkedAt ? row.check.chestnyZnak : null
  return choice === own ? SKIP : { patch: { chestnyZnak: choice } }
}

type NoteField = 'nonTariffNote' | 'certificateNote' | 'featuresNote'

export function parseNoteInput(
  field: NoteField,
  value: unknown,
  row: CustomsQueueRow,
): InlineParse {
  const next = text(value) || null
  const own = row.check?.[field] ?? null
  return next === own ? SKIP : { patch: { [field]: next } }
}

// ─── Редактор-выбор ─────────────────────────────────────────────────────────

interface ChoiceEditorParams {
  choices: Array<{ value: string; label: string }>
  currentOf: (row: CustomsGridRow) => string
}

/**
 * Выбор значения из короткого списка (сбор, «Честный знак»): клик — сохранить,
 * Esc — отмена. Выбранное коммитится после рендера — AG Grid берёт значение
 * редактора в stopEditing, а onValueChange применяется со следующим рендером.
 */
export function CustomsChoiceEditor(
  props: CustomCellEditorProps<CustomsGridRow, string> & ChoiceEditorParams,
) {
  const { choices, currentOf, data, onValueChange, stopEditing } = props
  const current = data ? currentOf(data) : ''
  const [picked, setPicked] = useState<string | null>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    selectedRef.current?.focus()
  }, [])
  useEffect(() => {
    if (picked !== null) stopEditing()
  }, [picked, stopEditing])

  return (
    <div
      role="listbox"
      className="min-w-[180px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
    >
      {choices.map((c) => (
        <button
          key={c.value}
          ref={c.value === current ? selectedRef : undefined}
          type="button"
          role="option"
          aria-selected={c.value === current}
          onClick={() => {
            onValueChange(c.value)
            setPicked(c.value)
          }}
          className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
            c.value === current ? 'font-medium text-brand-600' : 'text-gray-700'
          }`}
        >
          {c.label}
        </button>
      ))}
    </div>
  )
}

// ─── Колонки ────────────────────────────────────────────────────────────────

export type EditableField =
  | 'tnved'
  | 'duty'
  | 'fee'
  | 'znak'
  | 'nonTariffNote'
  | 'certificateNote'
  | 'featuresNote'

function parseFor(field: EditableField, value: unknown, row: CustomsQueueRow): InlineParse {
  switch (field) {
    case 'tnved':
      return parseTnvedInput(value, row)
    case 'duty':
      return parseDutyInput(value, row)
    case 'fee':
      return parseFeeInput(value, row)
    case 'znak':
      return parseZnakInput(value, row)
    default:
      return parseNoteInput(field, value, row)
  }
}

const EDIT_HINT = 'Двойной клик — изменить'

const DUTY_HINT = `${EDIT_HINT}: 8 или 7,5 — проценты; 0,08 — доля из файла брокера (тоже 8 %)`

/** Свойства колонки для правки логистом; без права правки — пусто. */
export function answerEditProps(
  field: EditableField,
  onEdit: CustomsEditHandler | undefined,
): Partial<ColDef<CustomsGridRow>> {
  if (!onEdit) return {}
  const valueSetter = (p: ValueSetterParams<CustomsGridRow>) => {
    if (!p.data) return false
    const parsed = parseFor(field, p.newValue, p.data.raw)
    if ('error' in parsed) toast.error(parsed.error)
    else if ('patch' in parsed) onEdit(p.data.raw, parsed.patch)
    return false
  }
  const base: Partial<ColDef<CustomsGridRow>> = {
    editable: true,
    valueSetter,
    headerTooltip: field === 'duty' ? DUTY_HINT : EDIT_HINT,
  }
  if (field === 'fee' || field === 'znak') {
    const params: ChoiceEditorParams =
      field === 'fee'
        ? { choices: FEE_CHOICES, currentOf: (r) => feeChoiceOf(r.raw) }
        : { choices: ZNAK_CHOICES, currentOf: (r) => znakOf(r.raw)?.value ?? 'UNKNOWN' }
    return {
      ...base,
      cellEditor: CustomsChoiceEditor,
      cellEditorPopup: true,
      cellEditorParams: params,
    }
  }
  if (field === 'tnved' || field === 'duty') return { ...base, cellEditor: 'agTextCellEditor' }
  // Тексты брокера — многострочный редактор; перенос строки — Shift/Alt+Enter.
  return {
    ...base,
    cellEditor: TextareaEditor,
    cellEditorPopup: true,
    cellEditorPopupPosition: 'over',
  }
}
